import { useState } from 'react'
import {
  Table, Group, TextInput, Title, Pagination, Badge, ActionIcon,
  Card, Text, Modal, Stack, Textarea, Button, Switch, NumberInput, MultiSelect, Select,
  Alert, Skeleton, Paper,
} from '@mantine/core'
import { IconSearch, IconEdit, IconTrash, IconPlus, IconAlertCircle, IconRefresh, IconPhoto, IconX } from '@tabler/icons-react'
import { useForm, Controller, useFieldArray, type Control } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useServices, useDepartments, useSvcCategories } from '@/lib/swr'
import type { Service, Department } from '@/types'

interface SectionFormData {
  title: string
  items: string[]
}

interface ServiceFormData {
  name: string
  category: string
  departments: string[]
  description: string
  descriptionSections: SectionFormData[]
  resultSections: SectionFormData[]
  duration: number
  price: number
  image: string
  status: 'active' | 'inactive'
}

const emptyForm: ServiceFormData = {
  name: '',
  category: '',
  departments: [],
  description: '',
  descriptionSections: [],
  resultSections: [],
  duration: 60,
  price: 0,
  image: '',
  status: 'active',
}

function SectionEditor({
  control,
  name,
  label,
  addLabel,
}: {
  control: Control<ServiceFormData>
  name: 'descriptionSections' | 'resultSections'
  label: string
  addLabel: string
}) {
  const { fields, append, remove } = useFieldArray({ control, name })

  return (
    <div>
      <Group justify="space-between" mb={6}>
        <Text size="sm" fw={500}>{label}</Text>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconPlus size={12} />}
          onClick={() => append({ title: '', items: [''] })}
        >
          {addLabel}
        </Button>
      </Group>
      <Stack gap="xs">
        {fields.map((field, index) => (
          <Paper key={field.id} p="sm" withBorder radius="md">
            <Stack gap={6}>
              <Group gap={6} align="flex-start">
                <Controller
                  control={control}
                  name={`${name}.${index}.title`}
                  render={({ field: f }) => (
                    <TextInput flex={1} placeholder="Дэд гарчиг" {...f} />
                  )}
                />
                <ActionIcon color="red" variant="light" onClick={() => remove(index)}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
              <Controller
                control={control}
                name={`${name}.${index}.items`}
                render={({ field: f }) => (
                  <Stack gap={4}>
                    {(f.value || []).map((item, itemIdx) => (
                      <Group key={itemIdx} gap={6}>
                        <TextInput
                          flex={1}
                          size="xs"
                          placeholder={`Жагсаалтын зүйл ${itemIdx + 1}`}
                          value={item}
                          onChange={(e) => {
                            const next = [...(f.value || [])]
                            next[itemIdx] = e.currentTarget.value
                            f.onChange(next)
                          }}
                        />
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => {
                            const next = [...(f.value || [])]
                            next.splice(itemIdx, 1)
                            f.onChange(next)
                          }}
                        >
                          <IconX size={12} />
                        </ActionIcon>
                      </Group>
                    ))}
                    <Button
                      size="xs"
                      variant="subtle"
                      leftSection={<IconPlus size={12} />}
                      onClick={() => f.onChange([...(f.value || []), ''])}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      Мөр нэмэх
                    </Button>
                  </Stack>
                )}
              />
            </Stack>
          </Paper>
        ))}
        {fields.length === 0 && (
          <Text size="xs" c="dimmed">Дэд гарчиг нэмэгдээгүй байна</Text>
        )}
      </Stack>
    </div>
  )
}

export default function ServiceList() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ServiceFormData>({ defaultValues: emptyForm })

  const watchImage = watch('image')

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true)
    try {
      const result = await api.admin.uploadImage(file)
      setValue('image', result.url, { shouldDirty: true })
      toast.success('Зураг байршуулагдлаа')
    } catch {
      toast.error('Зураг байршуулахад алдаа гарлаа')
    } finally {
      setUploadingImage(false)
    }
  }

  const queryParams: Record<string, string> = { page: String(page), limit: '20' }
  if (search) queryParams.search = search

  const { data: deptsData } = useDepartments()
  const departments: Department[] = (deptsData as any)?.data ?? []
  const deptOptions = departments.map((d) => ({ value: d._id, label: d.name }))
  const getDeptName = (id: string) => departments.find((d) => d._id === id)?.name ?? id

  const { data: svcCatsData } = useSvcCategories({ limit: '100' })
  const svcCategories = svcCatsData?.data ?? []
  const categoryOptions = svcCategories.map((c) => ({ value: c.slug, label: c.name }))
  const getCategoryName = (slug: string) => svcCategories.find((c) => c.slug === slug)?.name ?? slug

  const { data: servicesRes, isLoading: loading, error: swrError, mutate: reloadServices } = useServices(queryParams)
  const services: Service[] = (servicesRes as any)?.data ?? []
  const pagination = {
    page,
    pages: (servicesRes as any)?.pagination?.totalPages || (servicesRes as any)?.pagination?.pages || 1,
    total: (servicesRes as any)?.pagination?.total || 0,
  }
  const error = swrError ? (swrError instanceof Error ? swrError.message : 'Үйлчилгээнүүдийг ачаалахад алдаа гарлаа') : null

  const load = () => { reloadServices() }

  const openCreate = () => {
    setEditingId(null)
    reset(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (service: Service) => {
    setEditingId(service._id)
    reset({
      name: service.name,
      category: (service as any).category || '',
      departments: (service as any).departments || [],
      description: service.description || '',
      descriptionSections: ((service as any).descriptionSections || []).map((s: SectionFormData) => ({
        title: s.title || '',
        items: Array.isArray(s.items) ? s.items : [],
      })),
      resultSections: ((service as any).resultSections || []).map((s: SectionFormData) => ({
        title: s.title || '',
        items: Array.isArray(s.items) ? s.items : [],
      })),
      duration: service.duration,
      price: service.price,
      image: (service as any).image || '',
      status: service.status || 'active',
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    reset(emptyForm)
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const cleanSections = (sections: SectionFormData[]) =>
        sections
          .map((s) => ({
            title: (s.title || '').trim(),
            items: (s.items || []).map((i) => (i || '').trim()).filter((i) => i.length > 0),
          }))
          .filter((s) => s.title.length > 0 || s.items.length > 0)

      const data: Record<string, unknown> = {
        name: form.name,
        category: form.category || '',
        departments: form.departments,
        description: form.description || undefined,
        descriptionSections: cleanSections(form.descriptionSections || []),
        resultSections: cleanSections(form.resultSections || []),
        duration: Number(form.duration),
        price: Number(form.price),
        image: form.image || null,
        status: form.status,
      }

      if (editingId) {
        await api.admin.updateService(editingId, data)
        toast.success('Үйлчилгээ шинэчлэгдлээ')
      } else {
        await api.admin.createService(data)
        toast.success('Үйлчилгээ үүсгэгдлээ')
      }
      closeModal()
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteService(deleteId)
      toast.success('Үйлчилгээ устгагдлаа')
      load()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Устгахад алдаа гарлаа')
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Title order={2}>Үйлчилгээнүүд</Title>
          {pagination.total > 0 && <Badge variant="light" color="gray">{pagination.total}</Badge>}
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          Нэмэх
        </Button>
      </Group>

      <Group mb="md">
        <TextInput
          placeholder="Хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e?.currentTarget?.value ?? ''); setPage(1) }}
          w={300}
        />
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={18} />}
          title="Алдаа"
          color="red"
          variant="light"
          mb="md"
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />} onClick={load}>
              Дахин оролдох
            </Button>
          </Group>
        </Alert>
      )}

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Ангилал</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Тасаг</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хугацаа</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үнэ</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төлөв</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Table.Tr key={i}>
                    <Table.Td><Skeleton h={16} w={24} /></Table.Td>
                    <Table.Td><Skeleton h={16} /></Table.Td>
                    <Table.Td><Skeleton h={20} w={80} radius="xl" /></Table.Td>
                    <Table.Td><Skeleton h={16} w={70} /></Table.Td>
                    <Table.Td><Skeleton h={16} w={80} /></Table.Td>
                    <Table.Td><Skeleton h={20} w={70} radius="xl" /></Table.Td>
                    <Table.Td><Skeleton h={28} w={60} radius="md" /></Table.Td>
                  </Table.Tr>
                ))
              ) : (
                <>
                  {services.map((service, i) => (
                    <Table.Tr key={service._id}>
                      <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm" fw={500}>{service.name}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td>
                        {(service as any).category ? (
                          <Badge variant="light" size="sm" color="grape">{getCategoryName((service as any).category)}</Badge>
                        ) : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          {((service as any).departments || []).map((dId: string) => (
                            <Badge key={dId} variant="light" color="teal" size="sm">{getDeptName(dId)}</Badge>
                          ))}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{service.duration} мин</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{service.price.toLocaleString()}₮</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color={service.status === 'active' ? 'green' : 'gray'} size="sm">
                          {service.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6}>
                          <ActionIcon variant="light" color="blue" radius="md" onClick={() => openEdit(service)}>
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(service._id)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {!loading && !error && services.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={8}>
                        <Text ta="center" c="dimmed" py="xl">Үйлчилгээ олдсонгүй</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--app-border)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPage(p)} />
          </Group>
        )}
      </Card>

      {/* Delete Confirmation */}
      <Modal
        opened={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Устгах"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">Энэ үйлчилгээг устгахдаа итгэлтэй байна уу?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteId(null)}>Болих</Button>
            <Button color="red" onClick={handleDelete} loading={deleting} disabled={deleting}>Устгах</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Үйлчилгээ засах' : 'Шинэ үйлчилгээ'}
        centered
        size="lg"
      >
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="name"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Нэр шаардлагатай') }}
              render={({ field }) => (
                <TextInput
                  label="Нэр *"
                  placeholder="Зүү тавих эмчилгээ"
                  {...field}
                  required
                  error={errors.name?.message}
                />
              )}
            />
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Select
                  label="Ангилал"
                  placeholder="Ангилал сонгох"
                  data={categoryOptions}
                  value={field.value || null}
                  onChange={(v) => field.onChange(v || '')}
                  searchable
                  clearable
                />
              )}
            />
            <Controller
              name="departments"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label="Тасгууд"
                  placeholder="Тасаг сонгох"
                  data={deptOptions}
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  searchable
                />
              )}
            />
            <Group grow>
              <Controller
                name="duration"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="Хугацаа (минут)"
                    value={field.value}
                    onChange={(v) => field.onChange(Number(v) || 0)}
                    min={15}
                    max={480}
                  />
                )}
              />
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="Үнэ (₮)"
                    value={field.value}
                    onChange={(v) => field.onChange(Number(v) || 0)}
                    min={0}
                    thousandSeparator=","
                  />
                )}
              />
            </Group>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  label="Тайлбар"
                  placeholder="Үйлчилгээний дэлгэрэнгүй тайлбар..."
                  autosize
                  minRows={3}
                  {...field}
                />
              )}
            />
            <SectionEditor
              control={control}
              name="descriptionSections"
              label="Эмчилгээний тайлбар (дэд гарчиг + жагсаалт)"
              addLabel="Дэд гарчиг нэмэх"
            />
            <SectionEditor
              control={control}
              name="resultSections"
              label="Үр дүн (дэд гарчиг + жагсаалт)"
              addLabel="Дэд гарчиг нэмэх"
            />
            {/* Image upload */}
            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller
                  name="image"
                  control={control}
                  render={({ field }) => <TextInput flex={1} placeholder="Зурагны URL" {...field} value={field.value ?? ''} />}
                />
                <Button
                  size="sm" variant="light" leftSection={<IconPhoto size={14} />}
                  loading={uploadingImage}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = 'image/*'
                    input.onchange = () => input.files?.[0] && handleImageUpload(input.files[0])
                    input.click()
                  }}
                >
                  Upload
                </Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 80, borderRadius: 8, objectFit: 'cover' }} />}
            </div>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Switch
                  label="Идэвхтэй"
                  checked={field.value === 'active'}
                  onChange={(e) => field.onChange(e.target.checked ? 'active' : 'inactive')}
                />
              )}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {editingId ? 'Хадгалах' : 'Үүсгэх'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  )
}
